import { Hammer } from 'lucide-react';

const Products = () => {
  const products = [
    {
      title: 'Portones Corredizos',
      description: 'Portones metálicos con sistema de rieles para apertura lateral. Ideal para espacios reducidos y alta frecuencia de uso.',
      image: 'products/porton corredizo.jpg',
      features: ['Sistema manual o automático', 'Acabados personalizados', 'Rieles de alta resistencia'],
    },
    {
      title: 'Portones Batientes',
      description: 'Portones con apertura tradicional de una o dos hojas. Diseños clásicos y modernos disponibles.',
      image: 'products/porton batiente.jpg',
      features: ['Una o dos hojas', 'Cerraduras de seguridad', 'Diseños a medida'],
    },
    {
      title: 'Rejas para Ventanas',
      description: 'Rejas de seguridad con diseños modernos que no comprometen la estética de tu hogar o negocio.',
      image: 'products/Reja para ventanas.jpg',
      features: ['Diseños decorativos', 'Máxima seguridad', 'Tratamiento anticorrosivo'],
    },
    {
      title: 'Rejas para Puertas',
      description: 'Puertas de seguridad metálicas con sistemas de cierre múltiple para mayor protección.',
      image: 'products/puerta.png',
      features: ['Cierre multipunto', 'Ventilación integrada', 'Acabados duraderos'],
    },
    {
      title: 'Techos para Estacionamiento',
      description: 'Estructuras metálicas para cocheras con cubierta de policarbonato o chapa.',
      image: 'products/techo auto.jpg',
      features: ['Protección UV', 'Estructura reforzada', 'Instalación rápida'],
    },
    {
      title: 'Pérgolas Metálicas',
      description: 'Estructuras decorativas para patios y jardines con diseño minimalista.',
      image: 'products/pergola.jpg',
      features: ['Diseño moderno', 'Resistente a intemperie', 'Personalizable'],
    },
    {
      title: 'Escaleras Metálicas',
      description: 'Escaleras de hierro para interiores y exteriores con barandas de seguridad.',
      image: 'products/escaleras interiores.jpg',
      features: ['Diseño personalizado', 'Alta resistencia', 'Tratamiento antideslizante'],
    },
    {
      title: 'Estructuras a Medida',
      description: 'Proyectos especiales según las necesidades específicas de cada cliente.',
      image: 'https://images.unsplash.com/photo-1745448797900-35d08e85e9db?crop=entropy&cs=srgb&fm=jpg&q=85',
      features: ['Diseño único', 'Ingeniería estructural', 'Asesoramiento profesional'],
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20 md:py-32" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight uppercase mb-6">Nuestros Productos</h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto">
            Soluciones metálicas de alta calidad para cada necesidad. Todos nuestros productos están fabricados con materiales de primera y acabados profesionales.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 md:py-32 bg-white" data-testid="products-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div
                key={index}
                data-testid={`product-card-${index}`}
                className="group relative bg-white border border-slate-200 hover:border-slate-900 transition-all duration-300 flex flex-col h-full"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-slate-900 p-2 rounded-sm">
                    <Hammer className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <h3 className="text-2xl md:text-3xl font-semibold tracking-normal text-slate-900 mb-3">{product.title}</h3>
                  <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-6">{product.description}</p>
                  <div className="mt-auto">
                    <h4 className="text-sm font-medium tracking-widest uppercase text-slate-500 mb-3">Características:</h4>
                    <ul className="space-y-2">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="text-blue-700 mt-1">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specs Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="technical-specs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-4">Especificaciones Técnicas</h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Todos nuestros productos cumplen con los más altos estándares de calidad.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 border-l-4 border-blue-700">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Materiales</h3>
              <p className="text-base leading-relaxed text-slate-600">
                Utilizamos acero estructural de primera calidad, hierro forjado y aluminio de alta resistencia según el proyecto.
              </p>
            </div>
            <div className="bg-white p-8 border-l-4 border-blue-700">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Acabados</h3>
              <p className="text-base leading-relaxed text-slate-600">
                Tratamiento anticorrosivo, pintura electrostática, galvanizado en caliente y acabados personalizados.
              </p>
            </div>
            <div className="bg-white p-8 border-l-4 border-blue-700">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Garantía</h3>
              <p className="text-base leading-relaxed text-slate-600">
                Todos nuestros productos cuentan con garantía de fabricación y asesoramiento post-venta.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white" data-testid="products-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase mb-6">¿Necesitas un Presupuesto?</h2>
          <p className="text-lg md:text-xl text-slate-300 mb-8">
            Contáctanos para recibir asesoramiento personalizado y una cotización detallada sin compromiso.
          </p>
          <a
            href="/contacto"
            data-testid="products-contact-button"
            className="bg-white text-slate-900 hover:bg-slate-100 rounded-sm px-8 py-4 font-bold uppercase tracking-wider hover-lift hard-shadow inline-block"
          >
            Contactar Ahora
          </a>
        </div>
      </section>
    </div>
  );
};

export default Products;